import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { guideSchema, type GuideInput } from "@ckgo2/shared/schemas";
import { api, type ApiResponse } from "@/api/client";
import type { Guide } from "@ckgo2/shared/types";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import ImagePicker from "@/components/common/ImagePicker";

export default function GuideEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === "new";

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<GuideInput>({
    resolver: zodResolver(guideSchema),
    defaultValues: {
      name: "",
      slug: "",
      photo: "",
      bio: "",
      bioSk: "",
      specializations: [],
      yearsExperience: 0,
      isActive: true,
      sortOrder: 0,
    },
  });

  // Fetch guide if editing
  const { data: guide, isLoading } = useQuery({
    queryKey: ["guide", id],
    queryFn: () => api.get<ApiResponse<Guide>>(`/api/guides/${id}`),
    enabled: !isNew,
  });

  // Reset form when guide data loads
  useEffect(() => {
    if (guide?.data) {
      reset(guide.data);
    }
  }, [guide, reset]);

  // Auto-generate slug from name
  const name = watch("name");
  useEffect(() => {
    if (isNew && name) {
      const slug = name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setValue("slug", slug, { shouldDirty: false });
    }
  }, [name, isNew, setValue]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: GuideInput) => {
      if (isNew) {
        return api.post<ApiResponse<Guide>>("/api/admin/guides", data);
      }
      return api.put<ApiResponse<Guide>>(`/api/admin/guides/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guides"] });
      queryClient.invalidateQueries({ queryKey: ["guide", id] });
      navigate("/guides");
    },
  });

  const onSubmit = async (data: GuideInput) => {
    await saveMutation.mutateAsync(data);
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/guides"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "Nový průvodce" : "Upravit průvodce"}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew
                ? "Vytvořte nového průvodce"
                : `Úprava: ${guide?.data?.name}`}
            </p>
          </div>
        </div>

        <button
          type="submit"
          form="guide-form"
          disabled={isSubmitting || (!isDirty && !isNew)}
          className="btn-primary"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Ukládám...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" />
              Uložit
            </>
          )}
        </button>
      </div>

      {/* Error message */}
      {saveMutation.error && (
        <div className="rounded-lg bg-error-50 p-4 text-error-700 dark:bg-error-900/30 dark:text-error-400">
          {saveMutation.error.message}
        </div>
      )}

      {/* Form */}
      <form
        id="guide-form"
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-6 lg:grid-cols-3"
      >
        {/* Main content - 2 columns */}
        <div className="space-y-6 lg:col-span-2">
          {/* Basic info */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Základní informace
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              {/* Name */}
              <div>
                <label htmlFor="name" className="label">
                  Jméno *
                </label>
                <input
                  {...register("name")}
                  type="text"
                  id="name"
                  className={`input ${errors.name ? "input-error" : ""}`}
                  placeholder="např. Jan Novák"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Slug */}
              <div>
                <label htmlFor="slug" className="label">
                  URL slug *
                </label>
                <input
                  {...register("slug")}
                  type="text"
                  id="slug"
                  className={`input ${errors.slug ? "input-error" : ""}`}
                  placeholder="např. jan-novak"
                />
                {errors.slug && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.slug.message}
                  </p>
                )}
              </div>

              {/* Years Experience */}
              <div>
                <label htmlFor="yearsExperience" className="label">
                  Roky zkušeností
                </label>
                <input
                  {...register("yearsExperience", { valueAsNumber: true })}
                  type="number"
                  id="yearsExperience"
                  className="input"
                  min={0}
                />
              </div>

              {/* Living In */}
              <div>
                <label htmlFor="livingIn" className="label">
                  Bydliště
                </label>
                <input
                  {...register("livingIn")}
                  type="text"
                  id="livingIn"
                  className="input"
                  placeholder="Praha, Česká republika"
                />
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Životopis
            </h2>

            <div className="space-y-4">
              {/* Bio CZ */}
              <div>
                <label htmlFor="bio" className="label">
                  Životopis (CZ)
                </label>
                <textarea
                  {...register("bio")}
                  id="bio"
                  rows={5}
                  className="input"
                  placeholder="Krátký životopis průvodce..."
                />
              </div>

              {/* Bio SK */}
              <div>
                <label htmlFor="bioSk" className="label">
                  Životopis (SK)
                </label>
                <textarea
                  {...register("bioSk")}
                  id="bioSk"
                  rows={5}
                  className="input"
                  placeholder="Krátky životopis sprievodcu..."
                />
              </div>
            </div>
          </div>

          {/* Specializations */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Specializace
            </h2>

            <div>
              <label htmlFor="specializations" className="label">
                Specializace
              </label>
              <textarea
                id="specializations"
                rows={3}
                className="input"
                placeholder="Asie, Treking, Potápění"
                defaultValue={watch("specializations")?.join(", ")}
                onChange={(e) => {
                  const value = e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean);
                  setValue("specializations", value);
                }}
              />
              <p className="mt-1 text-xs text-gray-500">
                Oddělte čárkou
              </p>
            </div>
          </div>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Photo */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Fotografie
            </h2>

            <Controller
              name="photo"
              control={control}
              render={({ field }) => (
                <ImagePicker
                  value={field.value || ""}
                  onChange={field.onChange}
                  folder="guides"
                />
              )}
            />
          </div>

          {/* Settings */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Nastavení
            </h2>

            <div className="space-y-4">
              {/* Sort Order */}
              <div>
                <label htmlFor="sortOrder" className="label">
                  Pořadí řazení
                </label>
                <input
                  {...register("sortOrder", { valueAsNumber: true })}
                  type="number"
                  id="sortOrder"
                  className="input"
                  min={0}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Nižší číslo = vyšší priorita
                </p>
              </div>

              {/* Is Active */}
              <div className="flex items-center gap-3">
                <input
                  {...register("isActive")}
                  type="checkbox"
                  id="isActive"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label
                  htmlFor="isActive"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Aktivní průvodce
                </label>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
